'use client'

import type { SelectOption } from '.'

import { useControllableState } from '@radix-ui/react-use-controllable-state'
import { Command as CommandPrimitive } from 'cmdk'
import {
  type ComponentPropsWithoutRef,
  createContext,
  type ElementRef,
  forwardRef,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import Icon from '@/components/atomic/icon'
import { IconName } from '@/components/atomic/icon/types'
import { BodySmall, BodySubtext } from '@/components/atomic/text'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/shadcn/Command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/Popover'
import { cn } from '@/utils/shadcn/utils'

interface SelectContextApi {
  value?: string
  onValueChange: (value: string) => void
  open: boolean | undefined
  setOpen: (open: boolean) => void
  disabled?: boolean
  removeIcon?: boolean
  hideSearch?: boolean
}

const SelectContext = createContext<SelectContextApi | null>(null)

interface SelectProps {
  value?: string
  onValueChange?: (value: string) => void
  defaultValue?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  disabled?: boolean
  children: ReactNode
  removeIcon?: boolean
  hideSearch?: boolean
  modal?: boolean
}

const Select = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      value,
      onValueChange,
      defaultValue,
      open: controlledOpen,
      onOpenChange,
      disabled = false,
      children,
      removeIcon = false,
      hideSearch = false,
      modal = false,
    },
    _ref,
  ) => {
    const [_value, _setValue] = useControllableState({
      prop: value,
      defaultProp: defaultValue as string,
      onChange: onValueChange,
    })

    const [_open, _setOpen] = useControllableState({
      prop: controlledOpen,
      defaultProp: false,
      onChange: onOpenChange,
    })

    return (
      <SelectContext.Provider
        value={{
          value: _value,
          onValueChange: _setValue,
          open: _open,
          setOpen: _setOpen,
          disabled,
          removeIcon,
          hideSearch,
        }}
      >
        <Popover
          open={disabled ? false : _open}
          onOpenChange={_setOpen}
          modal={modal}
        >
          {children}
        </Popover>
      </SelectContext.Provider>
    )
  },
)
Select.displayName = 'Select'

function generateSlot({
  slotProps,
  ref,
}: {
  slotProps?: SelectTriggerSlotProps
  ref: RefObject<HTMLDivElement | null>
}) {
  const renderedLabel = slotProps?.label ? (
    // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
    slotProps.size === 'small' ? (
      <BodySubtext color={'text-secondary'} className={slotProps.className}>
        {slotProps.label}
      </BodySubtext>
    ) : (
      <BodySmall color={'text-secondary'} className={slotProps.className}>
        {slotProps.label}
      </BodySmall>
    )
  ) : null

  return (
    <div
      ref={ref}
      className={`absolute top-0 bottom-0 flex items-center ${
        slotProps?.side === 'left' ? 'left-0' : 'right-0'
      }`}
    >
      <div
        className={cn(
          'w-full h-full flex items-center px-3',
          slotProps?.side === 'left' && 'border-r border-primary-border',
          slotProps?.side === 'right' && 'border-l border-primary-border',
        )}
      >
        <div className="relative flex items-center justify-center whitespace-nowrap">
          {slotProps?.icon ? slotProps.icon : renderedLabel}
          {slotProps?.showSeparator && (
            <div
              className={`absolute w-[1px] bg-moment-blue-grey-200 dark:bg-moment-blue-grey-1000`}
              style={{
                height: slotProps.separatorHeight,
                [slotProps.side === 'left' ? 'right' : 'left']: 0,
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

interface SelectTriggerSlotProps {
  label?: string
  icon?: React.ReactNode
  side?: 'left' | 'right' | 'both'
  showSeparator?: boolean
  separatorHeight?: string
  size?: 'small' | 'large'
  className?: string
}

interface SelectTriggerProps
  extends ComponentPropsWithoutRef<typeof PopoverTrigger> {
  slotProps?: SelectTriggerSlotProps
  error?: boolean
  triggerWrapperClassName?: string
}

const SelectTrigger = forwardRef<HTMLButtonElement, SelectTriggerProps>(
  (
    {
      className,
      children,
      slotProps,
      error,
      triggerWrapperClassName,
      ...props
    },
    ref,
  ) => {
    const select = useContext(SelectContext)
    if (!select) throw new Error('SelectTrigger must be used within Select')

    const slotRef = useRef<HTMLDivElement | null>(null)
    const [slotPadding, setSlotPadding] = useState<number>(0)
    useLayoutEffect(() => {
      if (slotRef.current && slotRef.current.offsetWidth !== slotPadding) {
        setSlotPadding(slotRef.current.offsetWidth)
      }
    }, [slotPadding])

    return (
      <PopoverTrigger
        ref={ref}
        className={cn(
          `w-full flex items-center justify-between whitespace-nowrap
        rounded-md border border-primary-border gap-2 pr-3
        hover:border-moment-teal-400 dark:hover:border-moment-teal-800
        focus:outline-hidden focus:ring-2 focus:border-1
        focus:ring-moment-teal-500 focus:border-moment-teal-600
        data-[state=open]:outline-hidden data-[state=open]:ring-2 data-[state=open]:border-1
        data-[state=open]:ring-moment-teal-500 data-[state=open]:border-moment-teal-600
        dark:data-[state=open]:ring-moment-teal-900 dark:data-[state=open]:border-moment-teal-500
        dark:focus:ring-moment-teal-900 dark:focus:border-moment-teal-500
        cursor-pointer
        disabled:cursor-not-allowed
        disabled:border-primary-border disabled:bg-moment-white-200
      dark:disabled:bg-moment-black-200
        hover:z-10 focus:z-10 data-[state=open]:z-10
        [&>span]:line-clamp-1
        [&.error]:outline-hidden [&.error]:ring-2 [&.error]:border-1 [&.error]:border-moment-red-600 [&.error]:ring-moment-red-200 dark:[&.error]:border-moment-red-400 dark:[&.error]:ring-moment-red-900`,
          error ? 'error' : '',
          className,
        )}
        disabled={select.disabled}
        {...props}
      >
        <div className="flex w-full h-full justify-between">
          <div
            className="flex items-center relative h-full grow overflow-auto"
            style={{
              paddingLeft:
                slotProps?.side === 'left' ? `${slotPadding}px` : undefined,
            }}
          >
            {slotProps?.side === 'left' &&
              generateSlot({ slotProps, ref: slotRef })}
            <div
              className={cn(
                triggerWrapperClassName
                  ? triggerWrapperClassName
                  : 'flex pl-3 w-full',
              )}
            >
              {children}
            </div>
          </div>

          {!select.removeIcon && (
            <div className="flex items-center w-[16px] ml-[3px]">
              <Icon
                iconName={IconName.ChevronDownIcon}
                className={cn(
                  'transition-transform duration-200 ease-in-out',
                  select.open && 'rotate-180',
                  select.disabled
                    ? 'text-moment-grey-400 dark:text-moment-grey-700'
                    : '',
                )}
              />
            </div>
          )}
        </div>
      </PopoverTrigger>
    )
  },
)
SelectTrigger.displayName = 'SelectTrigger'

const SelectInput = forwardRef<
  ElementRef<typeof CommandPrimitive.Input>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(
  (
    {
      className,
      value,
      defaultValue,
      onValueChange,
      placeholder = 'Search...',
      ...props
    },
    ref,
  ) => {
    const select = useContext(SelectContext)
    if (!select) throw new Error('SelectInput must be used within Select')

    const [search, setSearch] = useControllableState({
      prop: value,
      defaultProp: defaultValue as string,
      onChange: onValueChange,
    })

    useEffect(() => {
      if (select.open) {
        setSearch('')
      }
    }, [select.open, setSearch])

    return (
      <div className="flex items-center border-b border-primary-border px-3">
        <CommandPrimitive.Input
          ref={ref}
          value={search}
          onValueChange={setSearch}
          placeholder={placeholder}
          className={cn(
            'h-9 px-1.5 text-sm w-full bg-transparent outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          onKeyUp={(e) => {
            if (e.key === ' ') {
              e.preventDefault()
            }
          }}
          onBlur={(e) => {
            e.currentTarget.focus({ preventScroll: true })
          }}
          disabled={select.disabled}
          {...props}
        />
      </div>
    )
  },
)
SelectInput.displayName = 'SelectInput'

const SelectContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof PopoverContent> & {
    placeholder?: string
    options: SelectOption[]
    customEmptyMessage?: string | React.ReactNode
    onSearchChange?: (search: string) => void
    ignoreClientSideFiltering?: boolean
    commandClassName?: string
    groupClassName?: string
    searchClassName?: string
  }
>(
  (
    {
      className,
      children,
      placeholder = 'Search...',
      options,
      customEmptyMessage,
      onSearchChange,
      ignoreClientSideFiltering,
      commandClassName,
      groupClassName,
      searchClassName,
      ...props
    },
    ref,
  ) => {
    const select = useContext(SelectContext)
    if (!select) throw new Error('SelectContent must be used within Select')

    const commandRef = useRef<HTMLDivElement>(null)

    // Focus selected item when dropdown opens
    useEffect(() => {
      if (!select.open || !select.value) return

      const timeoutId = setTimeout(() => {
        // Use commandRef to scope the search instead of document for better performance
        const selectedItem = commandRef.current?.querySelector(
          `[cmdk-item][data-value="${select.value}"]`,
        )
        if (selectedItem) {
          const element = selectedItem as HTMLElement

          // Highlight selected item
          element.setAttribute('aria-selected', 'true')

          // Focus and scroll to center
          element.focus()
          element.scrollIntoView({ behavior: 'instant', block: 'center' })
        }
      }, 0)

      return () => clearTimeout(timeoutId)
    }, [select.open, select.value])

    const filter = useCallback(
      (value: string, search: string) => {
        const option = options.find(
          (option) => option.value.toString() === value,
        )
        if (!option) return 0
        if (value.includes(search)) return 1
        if (
          typeof option.label === 'string' &&
          option.label.toLowerCase().includes(search.toLowerCase())
        ) {
          return 1
        }
        return 0
      },
      [options],
    )

    return (
      <PopoverContent
        ref={ref}
        className={cn(
          'p-0 w-fit bg-background shadow-xs dark:shadow-md',
          className,
        )}
        align="start"
        {...props}
      >
        <Command
          className={cn('w-full', commandClassName)}
          filter={ignoreClientSideFiltering ? undefined : filter}
          shouldFilter={!ignoreClientSideFiltering}
          ref={commandRef}
        >
          {!select.hideSearch && (
            <>
              <SelectInput
                placeholder={placeholder}
                className={cn('h-9', searchClassName)}
                onValueChange={onSearchChange}
              />
              <CommandEmpty>
                {customEmptyMessage || 'No results found.'}
              </CommandEmpty>
            </>
          )}
          <CommandGroup
            className={cn('max-h-60 overflow-auto', groupClassName)}
          >
            {children}
          </CommandGroup>
        </Command>
      </PopoverContent>
    )
  },
)
SelectContent.displayName = 'SelectContent'

const SelectItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof CommandItem> & {
    value: string
    displayValue?: string | ReactNode
    subtext?: string | ReactNode
    rightContent?: string | ReactNode
    size?: 'small' | 'large'
  }
>(
  (
    { className, value, displayValue, subtext, rightContent, size, ...props },
    ref,
  ) => {
    const select = useContext(SelectContext)
    if (!select) throw new Error('SelectItem must be used within Select')

    const label = displayValue ?? value

    return (
      <CommandItem
        ref={ref}
        value={value}
        onSelect={(value) => {
          select.onValueChange(value)
          select.setOpen(false)
        }}
        className={cn(
          `relative flex w-full cursor-pointer select-none items-center
         rounded-sm outline-hidden px-2 py-1.5
         hover:bg-moment-black-hover dark:hover:bg-moment-white-hover
         focus:bg-moment-teal-200 dark:focus:bg-moment-teal-1000
         aria-selected:bg-moment-teal-200 dark:aria-selected:bg-moment-teal-1000`,
          className,
        )}
        {...props}
      >
        <div className="flex items-center justify-between w-full">
          <div className="grow">
            {typeof label === 'string' ? (
              // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
              size === 'large' ? (
                <BodySmall>{label}</BodySmall>
              ) : (
                <BodySubtext>{label}</BodySubtext>
              )
            ) : (
              <div
                className={
                  size === 'large'
                    ? 'text-[13px] leading-[24px] tracking-[-0.0025em] dark:tracking-[0.0025em] font-normal text-moment-teal-text'
                    : 'text-[12px] leading-[16px] tracking-[0em] dark:tracking-[0.005em] font-normal text-moment-teal-text'
                }
              >
                {label}
              </div>
            )}
            {subtext && (
              <div className="mt-0.5">
                {typeof subtext === 'string' ? (
                  <BodySubtext color="text-secondary">{subtext}</BodySubtext>
                ) : (
                  subtext
                )}
              </div>
            )}
          </div>
          {rightContent && (
            <div className="ml-2 flex items-center">
              {typeof rightContent === 'string' ? (
                <BodySubtext color="text-secondary">{rightContent}</BodySubtext>
              ) : (
                rightContent
              )}
            </div>
          )}
        </div>
      </CommandItem>
    )
  },
)
SelectItem.displayName = 'SelectItem'

export { Select, SelectContent, SelectItem, SelectTrigger }
